import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import { Card, CardHeader, CardContent, CardTitle } from "../components/Card";

const weekDays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const dayLabels = {
  monday: "Lundi",
  tuesday: "Mardi",
  wednesday: "Mercredi",
  thursday: "Jeudi",
  friday: "Vendredi",
  saturday: "Samedi",
  sunday: "Dimanche",
};

function getTimeOptions() {
  const options = [];
  for (let h = 0; h < 24; h++) {
    options.push(`${h.toString().padStart(2, "0")}:00`);
    options.push(`${h.toString().padStart(2, "0")}:30`);
  }
  return options;
}

export default function AvailabilitiesAdmin() {
  // Recherche et sélection user
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  // Pour edition disponibilité
  const [avail, setAvail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const timeOptions = getTimeOptions();

  // Recherche
  useEffect(() => {
    async function fetchUsers() {
      const snap = await getDocs(collection(db, "users"));
      setAllUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }
    fetchUsers();
  }, []);

  useEffect(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) {
      setSearchResults(allUsers);
      return;
    }
    setSearchResults(
      allUsers.filter(
        (u) =>
          (u.firstname && u.firstname.toLowerCase().includes(q)) ||
          (u.lastname && u.lastname.toLowerCase().includes(q)) ||
          (u.email && u.email.toLowerCase().includes(q))
      )
    );
  }, [userSearch, allUsers]);

  // Charger/dispo d'un user
  useEffect(() => {
    if (!selectedUser) {
      setAvail(null);
      return;
    }
    async function fetchAvail() {
      setLoading(true);
      const ref = doc(db, "availabilities", selectedUser.id);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setAvail(snap.data());
      } else {
        // Init structure
        setAvail({
          userId: selectedUser.id,
          availability: weekDays.reduce((acc, d) => {
            acc[d] = { periods: [] };
            return acc;
          }, {}),
        });
      }
      setLoading(false);
    }
    fetchAvail();
  }, [selectedUser]);

  // Cocher/décocher un jour
  function handleToggle(day) {
    setAvail((a) => {
      const copy = { ...a, availability: { ...a.availability } };
      if (copy.availability[day]) {
        // On retire complètement le jour
        delete copy.availability[day];
      } else {
        copy.availability[day] = { periods: [{ start: "09:00", end: "17:00" }] };
      }
      return copy;
    });
  }

  // Modifier horaire d'un jour
  function handleTimeChange(day, which, val) {
    setAvail((a) => {
      const copy = { ...a };
      if (copy.availability[day].periods.length === 0) {
        copy.availability[day] = {
          periods: [{ start: "09:00", end: "17:00" }],
        };
      }
      copy.availability[day].periods[0][which] = val;
      return copy;
    });
  }

  // Sauvegarde
  async function save() {
    setSaving(true);
    try {
      const ref = doc(db, "availabilities", selectedUser.id);
      await setDoc(ref, {
        ...avail,
        updatedAt: new Date().toISOString(),
      });

      // Success notification
      const successDiv = document.createElement("div");
      successDiv.className =
        "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2";
      successDiv.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        Disponibilités sauvegardées avec succès !
      `;
      document.body.appendChild(successDiv);
      setTimeout(() => successDiv.remove(), 3000);
    } catch {
      alert("Erreur lors de la sauvegarde");
    }
    setSaving(false);
  }

  return (
    <PageTemplate
      loading={false}
      content={
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Info Card */}
          <Card className="bg-gradient-to-r from-pink-50 to-rose-50 border-pink-200">
            <CardContent className="flex items-start gap-4">
              <div className="p-3 bg-[#ff9be4] rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 text-white"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Gestion des disponibilités
                </h3>
                <p className="text-sm text-gray-600">
                  Configurez les créneaux horaires disponibles pour chaque utilisateur.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* User Search Card */}
          <Card>
            <CardHeader>
              <CardTitle>Rechercher un utilisateur</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <input
                  value={userSearch}
                  onChange={(e) => {
                    setUserSearch(e.target.value);
                    setSelectedUser(null);
                  }}
                  placeholder="Rechercher par prénom, nom ou email..."
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pl-10 focus:outline-none focus:ring-2 focus:ring-[#ff9be4] focus:border-transparent"
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5 text-gray-400 absolute left-3 top-3"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                  />
                </svg>
              </div>

              {userSearch && !selectedUser && (
                <div className="mt-3 border border-gray-200 rounded-lg bg-white max-h-64 overflow-y-auto">
                  {searchResults.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-pink-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
                      onClick={() => {
                        setSelectedUser(u);
                        setUserSearch(`${u.firstname} ${u.lastname} (${u.email})`);
                      }}
                    >
                      {u.profilPhotoUrl ? (
                        <img
                          src={u.profilPhotoUrl}
                          alt={`${u.firstname} ${u.lastname}`}
                          className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-200"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gradient-to-br from-[#ff9be4] to-[#ff6bd4] rounded-full flex items-center justify-center text-white font-semibold">
                          {u.firstname?.[0]?.toUpperCase() || "?"}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {u.firstname} {u.lastname}
                        </p>
                        <p className="text-xs text-gray-500">{u.email}</p>
                      </div>
                    </div>
                  ))}
                  {!searchResults.length && (
                    <div className="px-4 py-8 text-center text-gray-400">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-12 h-12 mx-auto mb-2 text-gray-300"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
                        />
                      </svg>
                      Aucun utilisateur trouvé
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Availability Editor */}
          {selectedUser && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>
                      Disponibilités de {selectedUser.firstname}{" "}
                      {selectedUser.lastname}
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedUser.email}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedUser(null);
                      setUserSearch("");
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5 text-gray-500"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18 18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <svg
                      className="animate-spin h-8 w-8 text-[#ff9be4]"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </div>
                ) : (
                  <form
                    className="space-y-3"
                    onSubmit={(e) => {
                      e.preventDefault();
                      save();
                    }}
                  >
                    {weekDays.map((day) => {
                      if (!avail || !avail.availability) return null;
                      const checked = avail.availability[day]?.periods?.length > 0;
                      const period = avail.availability[day]?.periods?.[0] || {
                        start: "09:00",
                        end: "17:00",
                      };
                      return (
                        <div
                          key={day}
                          className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${
                            checked
                              ? "bg-pink-50 border-[#ff9be4]"
                              : "bg-gray-50 border-gray-200"
                          }`}
                        >
                          <div className="flex items-center gap-3 min-w-[140px]">
                            <div
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
                                checked
                                  ? "bg-[#ff9be4] border-[#ff9be4]"
                                  : "border-gray-300 hover:border-[#ff9be4]"
                              }`}
                              onClick={() => handleToggle(day)}
                            >
                              {checked && (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  className="w-4 h-4 text-white"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>
                            <label
                              className={`font-medium cursor-pointer ${
                                checked ? "text-gray-900" : "text-gray-500"
                              }`}
                              onClick={() => handleToggle(day)}
                            >
                              {dayLabels[day]}
                            </label>
                          </div>
                          {checked && (
                            <div className="flex gap-3 items-center flex-1">
                              <select
                                value={period.start}
                                onChange={(e) =>
                                  handleTimeChange(day, "start", e.target.value)
                                }
                                className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#ff9be4] focus:border-transparent bg-white"
                              >
                                {timeOptions.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2}
                                stroke="currentColor"
                                className="w-5 h-5 text-gray-400"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                                />
                              </svg>
                              <select
                                value={period.end}
                                onChange={(e) =>
                                  handleTimeChange(day, "end", e.target.value)
                                }
                                className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#ff9be4] focus:border-transparent bg-white"
                              >
                                {timeOptions.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    <div className="pt-4 border-t border-gray-200 flex gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedUser(null);
                          setUserSearch("");
                        }}
                        className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                      >
                        Annuler
                      </button>
                      <button
                        type="submit"
                        disabled={saving}
                        className="flex-1 bg-[#ff9be4] text-white py-2.5 px-4 rounded-lg font-medium hover:bg-[#ff6bd4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {saving ? (
                          <>
                            <svg
                              className="animate-spin h-5 w-5"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Sauvegarde en cours...
                          </>
                        ) : (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-5 h-5"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                              />
                            </svg>
                            Sauvegarder les disponibilités
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      }
    />
  );
}